
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';

interface Job {
  id: string;
  job_title: string;
  company_name: string;
  contact_email: string;
}

interface JobApplicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  onApplicationSubmitted: () => void;
}

const JobApplicationModal = ({ open, onOpenChange, job, onApplicationSubmitted }: JobApplicationModalProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: user?.email || '',
    applicant_phone: '',
    cover_letter: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to apply for jobs');
      return;
    }

    if (!formData.applicant_name || !formData.applicant_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLoading(true);
    
    try {
      // Check if user has already applied for this job
      const { data: existingApplication, error: checkError } = await supabase
        .from('job_applications')
        .select('id')
        .eq('job_id', job.id)
        .eq('applicant_id', user.id)
        .single();

      if (existingApplication) {
        toast.error('You have already applied for this job');
        setLoading(false);
        return;
      }

      const applicationData = {
        job_id: job.id,
        applicant_id: user.id,
        applicant_name: formData.applicant_name,
        applicant_email: formData.applicant_email,
        applicant_phone: formData.applicant_phone || null,
        cover_letter: formData.cover_letter || null,
        application_status: 'pending'
      };

      const { error } = await supabase
        .from('job_applications')
        .insert([applicationData]);

      if (error) throw error;

      toast.success('Application submitted successfully!');
      
      // Reset form
      setFormData({
        applicant_name: '',
        applicant_email: user?.email || '',
        applicant_phone: '',
        cover_letter: ''
      });
      
      onApplicationSubmitted();
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-omoduduwa-terracotta">
            Apply for {job.job_title}
          </DialogTitle>
          <p className="text-sm text-gray-600">at {job.company_name}</p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="applicant_name">Full Name *</Label>
            <Input
              id="applicant_name"
              value={formData.applicant_name}
              onChange={(e) => handleInputChange('applicant_name', e.target.value)}
              placeholder="Your full name"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="applicant_email">Email Address *</Label>
            <Input
              id="applicant_email"
              type="email"
              value={formData.applicant_email}
              onChange={(e) => handleInputChange('applicant_email', e.target.value)}
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="applicant_phone">Phone Number</Label>
            <Input
              id="applicant_phone"
              value={formData.applicant_phone}
              onChange={(e) => handleInputChange('applicant_phone', e.target.value)}
              placeholder="+234 800 000 0000"
            />
          </div>

          <div>
            <Label htmlFor="cover_letter">Cover Letter</Label>
            <Textarea
              id="cover_letter"
              value={formData.cover_letter}
              onChange={(e) => handleInputChange('cover_letter', e.target.value)}
              placeholder="Tell us why you're interested in this position..."
              rows={4}
            />
          </div>

          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Your application will be sent directly to the employer at {job.contact_email}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationModal;
